import {
  CapacityUnit,
  LoadUnit,
  type BatteryInput,
  type LoadInput,
  type EfficiencyInput,
  type SafetyInput,
  type RuntimeResult,
  type BatterySizeResult,
  type MaxLoadResult,
  type CalculationMode,
  type ConfidenceLevel,
  type BatteryChemistry,
} from '@brt/shared-types'
import { getChemistryPreset } from '../data/chemistryPresets.js'
import {
  applyDoDDerating,
  applySoCDerating,
  applySoHDerating,
  applyTemperatureDerating,
  applySelfDischarge,
  computeConfidenceLevel,
  getTemperatureFactor,
} from './deratingEngine.js'
import { applyPeukertCorrection, validateCRate } from './chemistryEngine.js'
import { roundTo4 } from '../utils/rounding.js'
import {
  convertCapacity,
  convertLoad,
  isPowerBased,
  isCurrentBased,
} from '../utils/unitConversion.js'
import { formatRuntimeValue } from '../utils/formatRuntime.js'

const COMMON_BATTERY_SIZES = [7, 12, 18, 20, 35, 50, 75, 100, 150, 200, 300]

function sumLoadA(load: LoadInput, voltage: number): { totalA: number; totalW: number; error: string | null } {
  const enabledLoads = load.loads.filter((l) => l.enabled && l.loadValue > 0)

  if (enabledLoads.length === 0) {
    return { totalA: 0, totalW: 0, error: 'No enabled loads with positive values' }
  }

  let totalA = 0
  let totalW = 0

  for (const entry of enabledLoads) {
    if (isCurrentBased(entry.loadUnit)) {
      const a = convertLoad(entry.loadValue, entry.loadUnit, LoadUnit.A)
      totalA += a
      totalW += a * voltage
    } else {
      const w = convertLoad(entry.loadValue, entry.loadUnit, LoadUnit.W, voltage)
      totalW += w
      totalA += w / voltage
    }
  }

  return { totalA: roundTo4(totalA), totalW: roundTo4(totalW), error: null }
}

function countActiveDeratingFactors(battery: BatteryInput, efficiency: EfficiencyInput, safety: SafetyInput): number {
  let count = 0
  if (battery.dod < 1) count++
  if (battery.stateOfCharge < 1) count++
  if (battery.stateOfHealth < 1) count++
  if (battery.temperatureC !== 25) count++
  if (battery.storageDays > 0) count++
  if (efficiency.includeEfficiency && efficiency.efficiencyPercent < 1) count++
  if (safety.includeReserve && safety.reserveMarginPercent > 0) count++
  return count
}

function buildFormulaExplanation(
  rawAh: number,
  deratingSteps: RuntimeResult['deratingSteps'],
  deratingFactors: RuntimeResult['deratingFactors'],
  totalLoadA: number,
  effectiveLoadA: number,
  runtimeHours: number | null,
  efficiencyIncluded: boolean,
  reserveIncluded: boolean,
): string {
  const lines: string[] = []
  lines.push(`Raw Capacity: ${rawAh.toFixed(2)} Ah`)
  if (deratingFactors.dodFactor < 1) {
    lines.push(`× DoD (${deratingFactors.dodFactor.toFixed(2)}) = ${deratingSteps.afterDoD.toFixed(2)} Ah`)
  }
  if (deratingFactors.socFactor < 1) {
    lines.push(`× SoC (${deratingFactors.socFactor.toFixed(2)}) = ${deratingSteps.afterSoC.toFixed(2)} Ah`)
  }
  if (deratingFactors.sohFactor < 1) {
    lines.push(`× SoH (${deratingFactors.sohFactor.toFixed(2)}) = ${deratingSteps.afterSoH.toFixed(2)} Ah`)
  }
  if (deratingFactors.temperatureFactor < 1) {
    lines.push(`× Temp (${deratingFactors.temperatureFactor.toFixed(2)}) = ${deratingSteps.afterTemperature.toFixed(2)} Ah`)
  }
  if (deratingFactors.selfDischargeFactor < 1) {
    lines.push(`× Self-discharge (${deratingFactors.selfDischargeFactor.toFixed(3)}) = ${deratingSteps.afterSelfDischarge.toFixed(2)} Ah`)
  }
  if (deratingFactors.peukertFactor !== 1) {
    lines.push(`× Peukert (${deratingFactors.peukertFactor.toFixed(3)}) = ${deratingSteps.afterPeukert.toFixed(2)} Ah`)
  }
  lines.push(`Effective Capacity: ${deratingSteps.afterPeukert.toFixed(2)} Ah`)
  lines.push('')
  if (efficiencyIncluded) {
    lines.push(`Load: ${totalLoadA.toFixed(2)} A ÷ Efficiency = ${effectiveLoadA.toFixed(2)} A effective`)
  } else {
    lines.push(`Load: ${effectiveLoadA.toFixed(2)} A`)
  }
  if (reserveIncluded) {
    lines.push(`× Reserve margin: ${deratingSteps.afterReserve.toFixed(2)} Ah usable`)
  }
  if (runtimeHours !== null) {
    lines.push(`Runtime = ${deratingSteps.afterReserve.toFixed(2)} Ah ÷ ${effectiveLoadA.toFixed(2)} A = ${runtimeHours.toFixed(2)} h`)
  }
  return lines.join('\n')
}

function buildAssumptionsList(
  battery: BatteryInput,
  efficiency: EfficiencyInput,
  safety: SafetyInput,
  chemistry: BatteryChemistry,
): string[] {
  const assumptions: string[] = []
  const preset = getChemistryPreset(chemistry)

  assumptions.push(`Chemistry: ${chemistry} (Peukert exponent: ${preset.peukertExponent})`)

  if (battery.dod < preset.defaultDoD) {
    assumptions.push(`Custom DoD: ${(battery.dod * 100).toFixed(0)}% (default: ${(preset.defaultDoD * 100).toFixed(0)}%)`)
  } else {
    assumptions.push(`DoD: ${(battery.dod * 100).toFixed(0)}%`)
  }

  if (battery.stateOfCharge < 1) {
    assumptions.push(`State of Charge: ${(battery.stateOfCharge * 100).toFixed(0)}%`)
  }

  if (battery.stateOfHealth < 1) {
    assumptions.push(`State of Health: ${(battery.stateOfHealth * 100).toFixed(0)}%`)
  }

  if (battery.temperatureC !== 25) {
    assumptions.push(`Temperature: ${battery.temperatureC}°C`)
  }

  if (battery.storageDays > 0) {
    assumptions.push(`Storage: ${battery.storageDays} days`)
  }

  if (efficiency.includeEfficiency) {
    assumptions.push(`Efficiency: ${efficiency.efficiencyPercent}% (${efficiency.description})`)
  }

  if (safety.includeReserve && safety.reserveMarginPercent > 0) {
    assumptions.push(`Reserve margin: ${(safety.reserveMarginPercent * 100).toFixed(0)}%`)
  }

  return assumptions
}

export function calculateRuntime(
  battery: BatteryInput,
  load: LoadInput,
  efficiency: EfficiencyInput,
  safety: SafetyInput,
): RuntimeResult {
  const preset = getChemistryPreset(battery.chemistry)

  const rawCapacityAh = convertCapacity(
    battery.capacityValue,
    battery.capacityUnit,
    CapacityUnit.AH,
    battery.voltage,
  )

  const loadSum = sumLoadA(load, battery.voltage)

  if (loadSum.totalA <= 0) {
    return {
      runtimeHours: null,
      error: loadSum.error ?? 'Load must be greater than 0',
      rawCapacityAh,
      effectiveCapacityAh: 0,
      effectiveEnergyWh: 0,
      totalLoadA: 0,
      totalLoadW: 0,
      cRate: 0,
      calculationMode: 'CURRENT_BASED' as CalculationMode,
      deratingSteps: {
        afterDoD: 0, afterSoC: 0, afterSoH: 0, afterTemperature: 0,
        afterSelfDischarge: 0, afterPeukert: 0, afterEfficiency: 0, afterReserve: 0,
      },
      deratingFactors: {
        dodFactor: 1, socFactor: 1, sohFactor: 1, temperatureFactor: 1,
        selfDischargeFactor: 1, peukertFactor: 1, efficiencyFactor: 1, reserveFactor: 1,
      },
      confidence: 'ideal' as ConfidenceLevel,
      confidenceExplanation: 'No load provided',
      formulaExplanation: 'Enter a load value to calculate runtime',
      assumptionsList: [],
      runtimeFormatted: '—',
      runtimeMinutes: null,
      runtimeSeconds: null,
      effectiveLoadA: 0,
      effectiveLoadW: 0,
    }
  }

  const totalLoadA = loadSum.totalA
  const totalLoadW = loadSum.totalW

  const calculationMode: CalculationMode = load.loads.some((l) => isPowerBased(l.loadUnit))
    ? 'POWER_BASED'
    : 'CURRENT_BASED'

  let effectiveCapacity = rawCapacityAh

  const dodFactor = battery.dod
  effectiveCapacity = applyDoDDerating(effectiveCapacity, battery.dod)
  const afterDoD = roundTo4(effectiveCapacity)

  const socFactor = battery.stateOfCharge
  effectiveCapacity = applySoCDerating(effectiveCapacity, battery.stateOfCharge)
  const afterSoC = roundTo4(effectiveCapacity)

  const sohFactor = battery.stateOfHealth
  effectiveCapacity = applySoHDerating(effectiveCapacity, battery.stateOfHealth)
  const afterSoH = roundTo4(effectiveCapacity)

  const temperatureFactor = getTemperatureFactor(battery.temperatureC, battery.chemistry)
  effectiveCapacity = applyTemperatureDerating(effectiveCapacity, battery.temperatureC, battery.chemistry)
  const afterTemperature = roundTo4(effectiveCapacity)

  const selfDischargeFactor = battery.storageDays > 0
    ? (() => {
        const after = applySelfDischarge(1, battery.storageDays, battery.chemistry)
        return roundTo4(after)
      })()
    : 1.0
  effectiveCapacity = applySelfDischarge(effectiveCapacity, battery.storageDays, battery.chemistry)
  const afterSelfDischarge = roundTo4(effectiveCapacity)

  const peukertResult = applyPeukertCorrection(
    effectiveCapacity,
    totalLoadA,
    preset.peukertExponent,
    preset.ratedDischargeHours,
  )

  let peukertFactor = 1.0
  if (peukertResult.effectiveCapacityAh !== null && peukertResult.error === null) {
    peukertFactor = roundTo4(peukertResult.correctionFactor)
    effectiveCapacity = peukertResult.effectiveCapacityAh
  }
  const afterPeukert = roundTo4(effectiveCapacity)

  let effectiveLoadA = totalLoadA
  let efficiencyFactor = 1.0
  if (efficiency.includeEfficiency && efficiency.efficiencyPercent < 1) {
    efficiencyFactor = efficiency.efficiencyPercent
    effectiveLoadA = roundTo4(totalLoadA / efficiencyFactor)
  }

  const afterEfficiency = roundTo4(effectiveCapacity)

  let reserveFactor = 1.0
  if (safety.includeReserve && safety.reserveMarginPercent > 0) {
    reserveFactor = 1 - safety.reserveMarginPercent
    effectiveCapacity = roundTo4(effectiveCapacity * reserveFactor)
  }
  const afterReserve = roundTo4(effectiveCapacity)

  let runtimeHours: number | null = null
  let runtimeError: string | null = null

  if (effectiveLoadA > 0 && effectiveCapacity > 0) {
    runtimeHours = roundTo4(effectiveCapacity / effectiveLoadA)
    if (!Number.isFinite(runtimeHours)) {
      runtimeHours = null
      runtimeError = 'Calculation produced an invalid result'
    }
  } else if (effectiveLoadA <= 0) {
    runtimeError = 'Effective load must be greater than 0'
  } else {
    runtimeError = 'Effective capacity must be greater than 0'
  }

  const cRateResult = validateCRate(totalLoadA, rawCapacityAh, battery.chemistry)
  const deratingCount = countActiveDeratingFactors(battery, efficiency, safety)
  const hasPeukert = peukertFactor !== 1.0
  const confidenceResult = computeConfidenceLevel(
    deratingCount + (hasPeukert ? 1 : 0),
    load.dutyCycle?.useCustomDutyCycle ?? false,
  )

  const assumptionsList = buildAssumptionsList(battery, efficiency, safety, battery.chemistry)
  const formulaExplanation = buildFormulaExplanation(
    rawCapacityAh,
    {
      afterDoD, afterSoC, afterSoH, afterTemperature,
      afterSelfDischarge, afterPeukert, afterEfficiency, afterReserve,
    },
    {
      dodFactor, socFactor, sohFactor, temperatureFactor,
      selfDischargeFactor, peukertFactor, efficiencyFactor, reserveFactor,
    },
    totalLoadA,
    effectiveLoadA,
    runtimeHours,
    efficiency.includeEfficiency,
    safety.includeReserve,
  )

  return {
    runtimeHours,
    error: runtimeError,
    rawCapacityAh: roundTo4(rawCapacityAh),
    effectiveCapacityAh: afterReserve,
    effectiveEnergyWh: roundTo4(afterReserve * battery.voltage),
    totalLoadA,
    totalLoadW,
    cRate: cRateResult.cRate,
    calculationMode,
    deratingSteps: {
      afterDoD, afterSoC, afterSoH, afterTemperature,
      afterSelfDischarge, afterPeukert, afterEfficiency, afterReserve,
    },
    deratingFactors: {
      dodFactor, socFactor, sohFactor, temperatureFactor,
      selfDischargeFactor, peukertFactor, efficiencyFactor, reserveFactor,
    },
    confidence: confidenceResult.level,
    confidenceExplanation: confidenceResult.explanation,
    formulaExplanation,
    assumptionsList,
    runtimeFormatted: runtimeHours !== null ? formatRuntimeValue(runtimeHours) : '—',
    runtimeMinutes: runtimeHours !== null ? roundTo4(runtimeHours * 60) : null,
    runtimeSeconds: runtimeHours !== null ? roundTo4(runtimeHours * 3600) : null,
    effectiveLoadA,
    effectiveLoadW: roundTo4(effectiveLoadA * battery.voltage),
  }
}

export function solveForBatterySize(
  targetRuntimeHours: number,
  load: LoadInput,
  efficiency: EfficiencyInput,
  safety: SafetyInput,
  battery: BatteryInput,
): BatterySizeResult {
  const loadSum = sumLoadA(load, battery.voltage)
  if (loadSum.totalA <= 0 || targetRuntimeHours <= 0) {
    return {
      requiredCapacityAh: null,
      error: 'Load and target runtime must be greater than 0',
      effectiveLoadA: 0,
      targetRuntimeHours,
      totalDeratingFactor: 1,
      nearestSizes: [],
    }
  }

  const effectiveLoadA = efficiency.includeEfficiency
    ? roundTo4(loadSum.totalA / efficiency.efficiencyPercent)
    : loadSum.totalA

  let totalDeratingFactor = battery.dod
  totalDeratingFactor *= battery.stateOfCharge
  totalDeratingFactor *= battery.stateOfHealth

  totalDeratingFactor *= getTemperatureFactor(battery.temperatureC, battery.chemistry)

  if (battery.storageDays > 0) {
    totalDeratingFactor *= applySelfDischarge(1, battery.storageDays, battery.chemistry)
  }

  if (safety.includeReserve && safety.reserveMarginPercent > 0) {
    totalDeratingFactor *= (1 - safety.reserveMarginPercent)
  }

  let requiredCapacityAh = roundTo4(
    (effectiveLoadA * targetRuntimeHours) / totalDeratingFactor,
  )

  // Apply Peukert correction: adjust required capacity for the discharge rate
  const preset = getChemistryPreset(battery.chemistry)
  const peukertResult = applyPeukertCorrection(
    requiredCapacityAh,
    effectiveLoadA,
    preset.peukertExponent,
    preset.ratedDischargeHours,
  )
  if (peukertResult.effectiveCapacityAh !== null && peukertResult.error === null && peukertResult.correctionFactor !== 1) {
    // Peukert reduces effective capacity at higher loads, so we need more rated capacity to compensate
    requiredCapacityAh = roundTo4(requiredCapacityAh / peukertResult.correctionFactor)
  }

  const nearestSizes = COMMON_BATTERY_SIZES
    .filter((size) => size >= requiredCapacityAh * 0.5)
    .slice(0, 5)
    .map((size) => {
      const peukertForSize = applyPeukertCorrection(size, effectiveLoadA, preset.peukertExponent, preset.ratedDischargeHours)
      const effectiveCapForSize = (peukertForSize.effectiveCapacityAh ?? size) * totalDeratingFactor
      const estimatedRuntime = roundTo4(effectiveCapForSize / effectiveLoadA)
      return {
        capacityAh: size,
        label: `${size} Ah`,
        estimatedRuntimeHours: estimatedRuntime,
        passFail: estimatedRuntime >= targetRuntimeHours
          ? ('pass' as const)
          : estimatedRuntime >= targetRuntimeHours * 0.9
            ? ('marginal' as const)
            : ('fail' as const),
      }
    })

  return {
    requiredCapacityAh,
    error: null,
    effectiveLoadA,
    targetRuntimeHours,
    totalDeratingFactor: roundTo4(totalDeratingFactor),
    nearestSizes,
  }
}

export function solveForMaxLoad(
  targetRuntimeHours: number,
  battery: BatteryInput,
  efficiency: EfficiencyInput,
  safety: SafetyInput,
): MaxLoadResult {
  if (targetRuntimeHours <= 0) {
    return {
      maxLoadA: null,
      maxLoadW: null,
      error: 'Target runtime must be greater than 0',
      targetRuntimeHours,
    }
  }

  const rawCapacityAh = convertCapacity(
    battery.capacityValue,
    battery.capacityUnit,
    CapacityUnit.AH,
    battery.voltage,
  )

  let effectiveCapacity = rawCapacityAh
  effectiveCapacity = applyDoDDerating(effectiveCapacity, battery.dod)
  effectiveCapacity = applySoCDerating(effectiveCapacity, battery.stateOfCharge)
  effectiveCapacity = applySoHDerating(effectiveCapacity, battery.stateOfHealth)
  effectiveCapacity = applyTemperatureDerating(effectiveCapacity, battery.temperatureC, battery.chemistry)
  effectiveCapacity = applySelfDischarge(effectiveCapacity, battery.storageDays, battery.chemistry)

  if (safety.includeReserve && safety.reserveMarginPercent > 0) {
    effectiveCapacity *= (1 - safety.reserveMarginPercent)
  }

  let maxLoadA = roundTo4(effectiveCapacity / targetRuntimeHours)

  // Apply Peukert correction using the actual load (before efficiency adjustment)
  const preset = getChemistryPreset(battery.chemistry)
  const peukertResult = applyPeukertCorrection(
    rawCapacityAh,
    maxLoadA,
    preset.peukertExponent,
    preset.ratedDischargeHours,
  )
  if (peukertResult.effectiveCapacityAh !== null && peukertResult.error === null && peukertResult.correctionFactor !== 1) {
    // Peukert reduces effective capacity, reducing the max sustainable load
    const effectiveCapWithPeukert = effectiveCapacity * peukertResult.correctionFactor
    maxLoadA = roundTo4(effectiveCapWithPeukert / targetRuntimeHours)
  }

  // Apply efficiency last: actual max load = effective max load × efficiency
  if (efficiency.includeEfficiency && efficiency.efficiencyPercent > 0) {
    maxLoadA = roundTo4(maxLoadA * efficiency.efficiencyPercent)
  }

  if (!Number.isFinite(maxLoadA) || maxLoadA <= 0) {
    return {
      maxLoadA: null,
      maxLoadW: null,
      error: 'Cannot compute max load with given parameters',
      targetRuntimeHours,
    }
  }

  return {
    maxLoadA,
    maxLoadW: roundTo4(maxLoadA * battery.voltage),
    error: null,
    targetRuntimeHours,
  }
}

export function calculateDutyCycleAverage(
  activeCurrent: number,
  sleepCurrent: number,
  activeDurationMs: number,
  sleepDurationMs: number,
): { averageCurrentA: number; dutyCycleRatio: number; effectiveLoadA: number; description: string } {
  const totalDurationMs = activeDurationMs + sleepDurationMs

  if (totalDurationMs <= 0) {
    return {
      averageCurrentA: activeCurrent,
      dutyCycleRatio: 1,
      effectiveLoadA: activeCurrent,
      description: '100% active — no duty cycle',
    }
  }

  const dutyCycleRatio = activeDurationMs / totalDurationMs
  const averageCurrentA = roundTo4(
    (activeCurrent * activeDurationMs + sleepCurrent * sleepDurationMs) / totalDurationMs,
  )

  const activePercent = Math.round(dutyCycleRatio * 100)
  const description = `Duty cycle: ${activePercent}% active (${activeDurationMs}ms on, ${sleepDurationMs}ms sleep) → avg ${averageCurrentA.toFixed(3)}A`

  return {
    averageCurrentA,
    dutyCycleRatio: roundTo4(dutyCycleRatio),
    effectiveLoadA: averageCurrentA,
    description,
  }
}

export function solveRequiredEfficiency(
  targetHours: number,
  effectiveCapacityAh: number,
  loadA: number,
): number {
  if (targetHours <= 0 || effectiveCapacityAh <= 0 || loadA <= 0) {
    return NaN
  }
  return roundTo4((loadA * targetHours) / effectiveCapacityAh)
}
